import {
  putContractTiers,
  readContractTiers,
  type ContractTiersPut,
  type ContractTiersRead,
  type LongTier,
} from '@/api/core/native-long-month-pricing';
import { OptionSelect } from '@/pages/settings/option-select';
import { Button, Checkbox, Input } from '@mgrvtechgroup/admin-ds';
import { useEffect, useState } from 'react';
import { EditActionBar } from './edit-action-bar';
import { useCurrentCommand } from './presentation/use-current-command';
import type { PropertyRuntime } from './property-queries';

const CODES = ['S', 'M', 'L'] as const;
interface Row {
  on: boolean;
  min: string;
  max: string;
}
type Rows = Record<(typeof CODES)[number], Row>;
const rowsOf = (tiers: LongTier[]): Rows =>
  Object.fromEntries(
    CODES.map((code) => {
      const t = tiers.find((x) => x.code === code && x.deleted_at === null);
      return [
        code,
        { on: !!t, min: t ? String(t.min_duration) : '', max: t ? String(t.max_duration) : '' },
      ];
    }),
  ) as Rows;
const unitText = (unit: 'MONTH' | 'DAY') => (unit === 'MONTH' ? '개월' : '일');
const whole = /^[1-9]\d{0,8}$/;

/** 지점 전체가 공유하는 계약 구간(S/M/L). 월 가격 경로가 아니라 구간 전용 경로로 읽고 쓰며, 지워지는 구간을 쓰는 할인 규칙은 함께 해제한다. */
export function LongMonthTiersPanel({
  readEnabled = true,
  runtime,
  propertyId,
  stateKey,
  onChanged,
}: {
  readEnabled?: boolean;
  runtime: PropertyRuntime;
  propertyId: string;
  /** 월 가격 쪽 전체 비교값이 바뀌면 구간의 비교값도 낡으므로 다시 읽는다. */
  stateKey: string;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<Rows>(rowsOf([]));
  const [unit, setUnit] = useState<'MONTH' | 'DAY'>('MONTH');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const discard = () => {
    setEditing(false);
    setConfirm(false);
    setReason('');
  };
  const cmd = useCurrentCommand<ContractTiersRead, ContractTiersPut>({
    runtime,
    scope: `long-month-tiers:${propertyId}`,
    readEnabled,
    readKey: stateKey,
    bits: 1,
    domain: 'booking',
    read: () => readContractTiers(runtime.client, propertyId),
    write: (body) => putContractTiers(runtime.client, propertyId, body),
    validate: () => Promise.resolve(),
    onActorChanged: discard,
  });
  const read = cmd.current;
  const pending =
    cmd.attempt && !cmd.attempt.accepted ? (cmd.attempt.body as ContractTiersPut) : null;
  useEffect(() => {
    if (cmd.success) {
      discard();
      onChanged();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmd.success]);
  useEffect(() => {
    if (!cmd.current && !cmd.attempt) discard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmd.current, cmd.attempt]);

  const active = read?.tiers.filter((t) => t.deleted_at === null) ?? [];
  const chosen = CODES.filter((code) => rows[code].on);
  const rowError = (() => {
    if (chosen.length === 0)
      return '구간을 하나 이상 남겨 주세요. 모두 없애려면 먼저 관련 규칙을 확인하세요.';
    for (const code of chosen) {
      const { min, max } = rows[code];
      if (!whole.test(min) || !whole.test(max))
        return `구간 ${code}의 범위를 1 이상의 정수로 입력해 주세요.`;
      if (Number(min) > Number(max)) return `구간 ${code}의 최소가 최대보다 큽니다.`;
    }
    const sorted = chosen
      .map((code) => [Number(rows[code].min), Number(rows[code].max)] as const)
      .sort((a, b) => a[0] - b[0]);
    for (let i = 1; i < sorted.length; i++)
      if ((sorted[i]?.[0] ?? 0) <= (sorted[i - 1]?.[1] ?? 0)) return '구간 범위가 서로 겹칩니다.';
    return '';
  })();
  // 지워지는 구간을 쓰는 활성 규칙은 서버가 명시 해제 목록을 요구한다.
  const removed = active.filter((t) => !rows[t.code].on);
  const retireRules = (read?.referencing_rules ?? []).filter(
    (r) => r.deleted_at === null && removed.some((t) => t.id === r.term_tier_id),
  );
  const submit = () => {
    if (!read || rowError || cmd.busy) return;
    void cmd.submit({
      command_id: crypto.randomUUID(),
      reason: reason.trim(),
      expected: read.cas,
      tiers: chosen.map((code) => ({
        code,
        duration_unit: unit,
        min_duration: Number(rows[code].min),
        max_duration: Number(rows[code].max),
      })),
      retire_rule_ids: retireRules.map((r) => r.id),
    });
  };
  return (
    <section aria-label="계약 구간" className="body-sm space-y-4 border-t pt-4 leading-relaxed">
      <h4 className="font-medium">계약 구간 (지점 전체 공통)</h4>
      <p className="body-sm text-muted-foreground">
        구간은 이 지점의 모든 대상이 함께 씁니다. 바꾸면 이 지점의 다른 객실·슬롯 할인에도 영향이
        있습니다.
      </p>
      {cmd.error && (
        <p role="alert" className="body-sm">
          {cmd.error}
        </p>
      )}
      {!read && !cmd.error && <p role="status">계약 구간을 확인하고 있습니다.</p>}
      {!read && cmd.error && (
        <Button
          className="h-auto min-h-8 whitespace-normal"
          variant="outline"
          onClick={() => {
            void cmd.refresh();
          }}
        >
          계약 구간 다시 조회
        </Button>
      )}
      {read && (
        <>
          <ul aria-label="현재 계약 구간" className="body-sm">
            {active.length === 0 && <li>설정된 계약 구간이 없습니다.</li>}
            {active.map((t) => (
              <li key={t.id}>
                구간 {t.code}: {t.min_duration}~{t.max_duration}
                {unitText(t.duration_unit)}
              </li>
            ))}
          </ul>
          {cmd.success && (
            <p role="status">저장 후 구간을 다시 조회했습니다. 위가 서버의 현재 값입니다.</p>
          )}
          {!editing && (
            <Button
              className="h-auto min-h-8 whitespace-normal"
              variant="outline"
              disabled={cmd.busy}
              onClick={() => {
                setRows(
                  rowsOf(
                    pending
                      ? pending.tiers.map((t, i) => ({
                          ...t,
                          id: String(i),
                          revision: 1,
                          deleted_at: null,
                        }))
                      : read.tiers,
                  ),
                );
                setUnit(pending?.tiers[0]?.duration_unit ?? active[0]?.duration_unit ?? 'MONTH');
                setReason(pending?.reason ?? '');
                setConfirm(!!pending);
                setEditing(true);
              }}
            >
              계약 구간 수정
            </Button>
          )}
          {editing && (
            <div className="space-y-3" aria-label="계약 구간 입력">
              <div className="grid gap-1">
                <span className="body-sm">구간 단위 (모든 구간 공통)</span>
                <OptionSelect
                  aria-label="구간 단위"
                  className="w-40"
                  value={unit}
                  options={[
                    { value: 'MONTH', label: '개월' },
                    { value: 'DAY', label: '일' },
                  ]}
                  onValueChange={(next) => {
                    setUnit(next as 'MONTH' | 'DAY');
                    setConfirm(false);
                  }}
                />
              </div>
              {CODES.map((code) => (
                <div key={code} className="flex flex-wrap items-end gap-3">
                  <label className="flex items-center gap-2">
                    <Checkbox
                      checked={rows[code].on}
                      disabled={cmd.busy || !!pending}
                      onCheckedChange={(on) => {
                        setRows({ ...rows, [code]: { ...rows[code], on } });
                        setConfirm(false);
                      }}
                    />
                    <span className="body-sm">구간 {code} 사용</span>
                  </label>
                  {rows[code].on &&
                    (['min', 'max'] as const).map((key) => (
                      <label key={key} className="grid gap-1">
                        <span className="body-sm">
                          구간 {code} {key === 'min' ? '최소' : '최대'}({unitText(unit)})
                        </span>
                        <Input
                          className="w-24"
                          inputMode="numeric"
                          value={rows[code][key]}
                          disabled={cmd.busy || !!pending}
                          onChange={(e) => {
                            setRows({
                              ...rows,
                              [code]: { ...rows[code], [key]: e.target.value.trim() },
                            });
                            setConfirm(false);
                          }}
                        />
                      </label>
                    ))}
                </div>
              ))}
              {rowError && (
                <p role="alert" className="body-sm">
                  {rowError}
                </p>
              )}
              <label className="grid gap-1">
                <span className="body-sm">구간 변경 사유</span>
                <Input
                  value={reason}
                  disabled={cmd.busy || !!pending}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setConfirm(false);
                  }}
                />
              </label>
              {confirm ? (
                <div className="space-y-2" aria-label="계약 구간 변경 확인">
                  <p className="body-sm" role="status">
                    구간을{' '}
                    {chosen
                      .map((code) => `${code} ${rows[code].min}~${rows[code].max}${unitText(unit)}`)
                      .join(', ')}
                    (으)로 바꿉니다.
                  </p>
                  {retireRules.length > 0 && (
                    <p className="body-sm">
                      없어지는 구간을 쓰는 할인 규칙 {retireRules.length}개도 함께 해제됩니다.
                    </p>
                  )}
                  <EditActionBar label="계약 구간 저장 동작">
                    <Button
                      className="h-auto min-h-8 whitespace-normal"
                      disabled={cmd.busy}
                      onClick={submit}
                    >
                      {pending ? '같은 요청 다시 보내기' : '계약 구간 저장'}
                    </Button>
                  </EditActionBar>
                </div>
              ) : (
                <EditActionBar label="계약 구간 검토 동작">
                  <Button
                    className="h-auto min-h-8 whitespace-normal"
                    variant="outline"
                    disabled={!!rowError || !reason.trim() || cmd.busy}
                    onClick={() => {
                      setConfirm(true);
                    }}
                  >
                    구간 변경 내용·영향 확인
                  </Button>
                </EditActionBar>
              )}
              <Button
                className="h-auto min-h-8 whitespace-normal"
                variant="outline"
                disabled={cmd.busy || !!pending}
                onClick={discard}
              >
                구간 수정 취소
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
